"use client";

import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
} from "@mui/material";
import { ArrowForward, Launch, Code, FilterList } from "@mui/icons-material";
import Link from "next/link";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { CaseStudy } from "@/types/case-study";
import { shades } from "@/styles/theme";

export const runtime = "edge";

const categories = [
  "All",
  "Web Development",
  "Mobile Development",
  "E-commerce",
  "UI/UX Design",
  "Automation",
];

export default function Portfolio() {
  const theme = useTheme();
  const [portfolioItemsList, setPortfolioItemsList] = useState<CaseStudy[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filteredItems, setFilteredItems] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchPortfolio = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "caseStudies"),
        where("isActive", "==", true),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const portfolioData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        slug: doc.data().slug || doc.id,
        title: doc.data().title || "Untitled Case Study",
        client: doc.data().client || "",
        excerpt: doc.data().excerpt || "",
        industry: doc.data().industry || "",
        services: doc.data().services || [],
        duration: doc.data().duration || "",
        technologies: doc.data().technologies || [],
        githubUrl: doc.data().githubUrl || "",
        projectUrl: doc.data().projectUrl || "",
        featuredImage: doc.data().featuredImage || "",
        isFeatured: doc.data().isFeatured || false,
        challenge: doc.data().challenge || "",
        solution: doc.data().solution || {
          overview: "",
          steps: [],
        },
        results: doc.data().results || {
          metrics: [],
          additionalResults: [],
        },
        keyLearnings: doc.data().keyLearnings || [],
        testimonial: doc.data().testimonial || {
          quote: "",
          author: "",
          position: "",
          company: "",
        },
        gallery: doc.data().gallery || [],
        isActive: doc.data().isActive ?? true,
        createdAt: doc.data().createdAt || new Date().toISOString(),
        updatedAt: doc.data().updatedAt || new Date().toISOString(),
      })) as CaseStudy[];

      setPortfolioItemsList(portfolioData);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load blog posts. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio, retryCount]);

  // Filter items when category changes
  useEffect(() => {
    if (selectedCategory === "All") {
      setFilteredItems(portfolioItemsList);
    } else {
      setFilteredItems(
        portfolioItemsList.filter((item) =>
          item.services.includes(selectedCategory)
        )
      );
    }
  }, [selectedCategory, portfolioItemsList]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ pt: 20, pb: 8 }}>
        <Skeleton variant="rectangular" width="100%" height={100} />
        <Skeleton variant="text" sx={{ fontSize: "3rem", mb: 4 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ pt: 20, pb: 8 }}>
        <Typography variant="h4" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button
          aria-label="Retry fetching portfolio"
          variant="contained"
          onClick={() => {
            setRetryCount((prev) => prev + 1);
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  const handleCategoryChange = (
    event: React.MouseEvent<HTMLElement>,
    newCategory: string | null
  ) => {
    if (newCategory !== null) {
      setSelectedCategory(newCategory);
    }
  };

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          mb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ maxWidth: 800, mx: "auto" }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 3,
              }}
            >
              Our Portfolio
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
            >
              Discover our innovative solutions across web development, mobile
              apps, e-commerce, and digital transformation projects.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Category Filter */}
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <FilterList /> Filter by Category
          </Typography>
          <ToggleButtonGroup
            value={selectedCategory}
            exclusive
            onChange={handleCategoryChange}
            aria-label="portfolio categories"
            sx={{
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1,
              "& .MuiToggleButton-root": {
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 2,
                px: 3,
                py: 1,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            {categories.map((category) => (
              <ToggleButton
                key={category}
                value={category}
                aria-label={category}
              >
                {category}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Portfolio Grid */}
        <Grid container spacing={4}>
          {filteredItems.map((item) => (
            <Grid key={item.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={item.featuredImage}
                  alt={item.title}
                  sx={{
                    objectFit: "cover",
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                />
                <CardContent
                  sx={{
                    p: 4,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Categories */}
                  <Box sx={{ mb: 2 }}>
                    {item.services?.slice(0, 2).map((service, index) => (
                      <Chip
                        key={index}
                        label={service}
                        size="small"
                        sx={{
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          mr: 1,
                          mb: 1,
                        }}
                      />
                    ))}
                  </Box>

                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600, flexGrow: 1 }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{ mb: 2, fontStyle: "italic" }}
                  >
                    {item.client}
                  </Typography>

                  <Typography
                    color={`${shades.secondary[600]}`}
                    sx={{ mb: 3, flexGrow: 1 }}
                  >
                    {item.excerpt}
                  </Typography>

                  {/* Technologies */}
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                  >
                    {item.technologies?.slice(0, 3).map((tech, index) => (
                      <Chip
                        key={index}
                        label={tech}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          color: "text.primary",
                          fontSize: "0.75rem",
                        }}
                      />
                    ))}
                    {(item.technologies?.length ?? 0) > 3 && (
                      <Chip
                        label={`+${(item.technologies?.length ?? 0) - 3}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
                    <Button
                      aria-label={`View project details for ${item.title}`}
                      component={Link}
                      href={`/case-studies/${item.slug}`}
                      variant="contained"
                      startIcon={<ArrowForward />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        flex: 1,
                      }}
                    >
                      View Project
                    </Button>
                    {item.projectUrl && (
                      <Button
                        aria-label={`View project details for ${item.title}`}
                        href={item.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<Launch />}
                        sx={{
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          minWidth: "auto",
                        }}
                      ></Button>
                    )}
                    {item.githubUrl && (
                      <Button
                        aria-label={`View GitHub repository for ${item.title}`}
                        href={item.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<Code />}
                        sx={{
                          borderColor: theme.palette.secondary.main,
                          color: theme.palette.secondary.main,
                          minWidth: "auto",
                        }}
                      ></Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No projects found in this category
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Try selecting a different category or check back soon for new
              projects.
            </Typography>
            <Button
              aria-label="View all projects"
              variant="outlined"
              onClick={() => setSelectedCategory("All")}
            >
              View All Projects
            </Button>
          </Box>
        )}

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: "center",
            mt: 8,
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Start Your Project?
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
          >
            Let&apos;s work together to bring your vision to life. Your project
            could be featured here next!
          </Typography>
          <Button
            aria-label="Start your project"
            variant="contained"
            size="large"
            href="/contact"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              px: 4,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Start Your Project
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
